import { CalendarClock, CheckCircle2, Send, Timer } from "lucide-react";
import { dayjs } from "@/utils/date";
import type { MessageRequestTimelineEntry } from "../model";

function formatTimestamp(value: string | null): string {
  return value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—";
}

const STATUS_CHIP: Record<MessageRequestTimelineEntry["status"], { classes: string; dot: string }> = {
  acknowledged: { classes: "border-emerald-200 bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
  sent: { classes: "border-blue-200 bg-blue-50 text-blue-700", dot: "bg-blue-500" },
  failed: { classes: "border-red-200 bg-red-50 text-red-700", dot: "bg-red-500" },
  expired: { classes: "border-red-200 bg-red-50 text-red-700", dot: "bg-red-500" },
  pending: { classes: "border-amber-200 bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  processing: { classes: "border-amber-200 bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  cancelled: { classes: "border-neutral-200 bg-neutral-50 text-neutral-600", dot: "bg-neutral-400" },
};

/**
 * Renders one outgoing message request on the CODEC timeline: the payload type
 * chip with its delivery status, the scheduled/sent/acknowledged times, the
 * delivery attempts, and the last error when present.
 *
 * @param entry - The message request timeline entry to render.
 */
export function MessageRequestTimelineCard({ entry }: { entry: MessageRequestTimelineEntry }) {
  const statusChip = STATUS_CHIP[entry.status] ?? STATUS_CHIP.cancelled;
  const attemptsLabel =
    entry.maxAttempts != null ? `${entry.attempts ?? 0}/${entry.maxAttempts}` : String(entry.attempts ?? 0);

  return (
    <div className="relative pl-10">
      <span className="absolute left-3 top-4 inline-flex h-3 w-3 -translate-x-1/2 rounded-full border border-white bg-violet-500 shadow-sm" />
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900">{entry.label}</h3>
              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-500">
                {entry.payloadType}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-600">Comando enviado pela plataforma · {entry.statusLabel}</p>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${statusChip.classes}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${statusChip.dot}`} aria-hidden />
            {entry.statusLabel}
          </span>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <div className="flex items-start gap-3">
            <CalendarClock className="mt-1 h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Agendado</p>
              <p className="text-sm text-gray-600">{formatTimestamp(entry.scheduledAt)}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Send className="mt-1 h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Enviado</p>
              <p className="text-sm text-gray-600">{formatTimestamp(entry.sentAt)}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-1 h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Confirmado</p>
              <p className="text-sm text-gray-600">{formatTimestamp(entry.acknowledgedAt)}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <Timer className="h-4 w-4" />
            <span>
              Tentativas: <span className="font-medium text-gray-700">{attemptsLabel}</span>
            </span>
          </div>
          {entry.lastError ? <span className="font-medium text-red-600">Erro: {entry.lastError}</span> : null}
        </div>
      </div>
    </div>
  );
}
